import { useEffect, useState } from "react";
import { Nav } from "../components/Nav";
import { api, API_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { Copy, Check, RefreshCw, Eye, EyeOff, ArrowLeft, Key, Zap } from "lucide-react";
import { toast } from "sonner";

const LANGS = [
    { id: "env",   label: ".env"    },
    { id: "curl",  label: "cURL"    },
    { id: "node",  label: "Node.js" },
    { id: "react", label: "React SDK" },
    { id: "py",    label: "Python"  },
];

export default function AdminApiKeys() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [tenants, setTenants] = useState([]);
    const [visible, setVisible] = useState({});
    const [copied, setCopied] = useState({});

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) navigate("/dashboard");
    }, [user, loading, navigate]);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/tenants");
            setTenants(data.items || []);
        } catch (e) {
            toast.error("Failed to load API keys");
        }
    };

    useEffect(() => { if (user?.role === "admin") load(); }, [user]);

    const rotate = async (id) => {
        if (!window.confirm("Rotate this API key? Existing integrations will stop working until you update them.")) return;
        try {
            const { data } = await api.post(`/admin/tenants/${id}/rotate-key`);
            setTenants((ts) => ts.map((t) => t.tenant_id === id ? { ...t, api_key: data.api_key } : t));
            setVisible((v) => ({ ...v, [id]: true }));
            toast.success("API key rotated");
        } catch {
            toast.error("Failed to rotate");
        }
    };

    const doCopy = async (text, key) => {
        await navigator.clipboard.writeText(text);
        setCopied((c) => ({ ...c, [key]: true }));
        setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1500);
    };

    if (loading || !user || user.role !== "admin") {
        return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto max-w-6xl px-6 py-10">
                <Button asChild variant="ghost" size="sm" className="mb-6" data-testid="back-to-admin">
                    <Link to="/admin"><ArrowLeft className="mr-2 h-4 w-4" /> Back to admin</Link>
                </Button>

                <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin console</div>
                    <h1 className="mt-2 font-display text-4xl font-bold tracking-tighter" data-testid="apikeys-heading">
                        SocialPulse API keys
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                        Drop-in API keys for every child site in the SocialPulse ecosystem.
                        Copy the snippet, paste it into the site's server config, and start
                        creating short links + QR codes instantly.
                    </p>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <InfoCard icon={Key} label="Base URL" value={API_URL} testId="info-base-url" />
                    <InfoCard icon={Zap} label="Header name" value="X-API-Key" mono testId="info-header" />
                    <InfoCard icon={Zap} label="Endpoints" value="/api/v1/links · /api/v1/analytics/summary" testId="info-endpoints" />
                </div>

                <div className="mt-10 space-y-6">
                    {tenants.map((t) => {
                        const isVisible = visible[t.tenant_id];
                        const shown = isVisible ? t.api_key : (t.api_key || "").replace(/./g, "•").slice(0, 32);
                        return (
                            <div key={t.tenant_id} className="border-2 border-border bg-card" data-testid={`tenant-card-${t.slug}`}>
                                <div className="flex flex-col justify-between gap-4 border-b border-border p-6 sm:flex-row sm:items-center">
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Tenant</div>
                                        <div className="mt-1 flex items-center gap-3">
                                            <div className="font-display text-2xl font-bold">{t.name}</div>
                                            <code className="rounded-md border border-border bg-muted/50 px-2 py-0.5 font-mono text-xs text-muted-foreground">
                                                {t.slug}
                                            </code>
                                        </div>
                                        <div className="mt-2 flex gap-4 font-mono text-xs text-muted-foreground">
                                            <span>{t.links_count} links</span>
                                            <span>·</span>
                                            <span>{t.clicks_count} clicks</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outline" onClick={() => setVisible((v) => ({ ...v, [t.tenant_id]: !v[t.tenant_id] }))} data-testid={`toggle-${t.slug}`}>
                                            {isVisible ? <><EyeOff className="mr-2 h-4 w-4" /> Hide</> : <><Eye className="mr-2 h-4 w-4" /> Reveal</>}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => doCopy(t.api_key, `${t.tenant_id}-key`)} data-testid={`copy-key-${t.slug}`}>
                                            {copied[`${t.tenant_id}-key`] ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                                            Copy key
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => rotate(t.tenant_id)} data-testid={`rotate-${t.slug}`}>
                                            <RefreshCw className="mr-2 h-4 w-4" /> Rotate
                                        </Button>
                                    </div>
                                </div>

                                <div className="p-6">
                                    <div className="mb-4 flex items-center gap-3">
                                        <code className="flex-1 truncate rounded-md border border-border bg-background px-3 py-2 font-mono text-sm">
                                            {shown}
                                        </code>
                                    </div>

                                    <Tabs defaultValue="env" className="w-full">
                                        <TabsList data-testid={`snippets-tabs-${t.slug}`}>
                                            {LANGS.map((l) => (
                                                <TabsTrigger key={l.id} value={l.id} data-testid={`tab-${l.id}-${t.slug}`}>
                                                    {l.label}
                                                </TabsTrigger>
                                            ))}
                                        </TabsList>
                                        {LANGS.map((l) => (
                                            <TabsContent key={l.id} value={l.id} className="mt-3">
                                                <SnippetBlock
                                                    code={snippetFor(l.id, t, API_URL)}
                                                    testId={`snippet-${l.id}-${t.slug}`}
                                                    onCopy={() => doCopy(snippetFor(l.id, t, API_URL), `${t.tenant_id}-${l.id}`)}
                                                    copied={copied[`${t.tenant_id}-${l.id}`]}
                                                />
                                            </TabsContent>
                                        ))}
                                    </Tabs>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function InfoCard({ icon: Icon, label, value, mono, testId }) {
    return (
        <div className="border border-border bg-card p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                {label}
            </div>
            <div className={`mt-2 break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
        </div>
    );
}

function SnippetBlock({ code, onCopy, copied, testId }) {
    return (
        <div className="relative">
            <pre
                className="max-h-[320px] overflow-auto whitespace-pre-wrap break-all rounded-md border border-border bg-[hsl(224,15%,7%)] p-4 pr-14 font-mono text-xs leading-relaxed text-[hsl(210,20%,90%)]"
                data-testid={testId}
            >{code}</pre>
            <button
                type="button"
                onClick={onCopy}
                className="absolute right-2 top-2 inline-flex h-8 items-center gap-1 rounded-md border border-[hsl(224,12%,25%)] bg-[hsl(224,12%,15%)] px-2 text-xs text-[hsl(210,20%,90%)] hover:bg-[hsl(224,12%,20%)]"
                aria-label="Copy snippet"
            >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
            </button>
        </div>
    );
}

function snippetFor(lang, t, apiBase) {
    const envName = `SPLINK_${t.slug.toUpperCase()}_API_KEY`;
    const baseNoApi = apiBase.replace(/\/api$/, "");
    switch (lang) {
        case "env":
            return [
                `# Add to your ${t.slug} site's .env`,
                `SPLINK_API_URL=${baseNoApi}`,
                `SPLINK_API_KEY=${t.api_key}`,
                `# tenant: ${t.slug} (${t.name})`,
            ].join("\n");
        case "curl":
            return [
                `curl -X POST "${apiBase}/v1/links" \\`,
                `  -H "Content-Type: application/json" \\`,
                `  -H "X-API-Key: ${t.api_key}" \\`,
                `  -d '{"long_url": "https://${t.slug}.example.com/page/1"}'`,
            ].join("\n");
        case "node":
            return [
                `// npm i @socialpulse/splink   (or use plain fetch)`,
                `import { createSplinkClient } from "@socialpulse/splink";`,
                ``,
                `const splink = createSplinkClient({`,
                `  baseUrl: "${baseNoApi}",`,
                `  apiKey: process.env.${envName},   // ${t.slug}`,
                `});`,
                ``,
                `const { short_url, slug } = await splink.createShortLink({`,
                `  long_url: "https://${t.slug}.example.com/page/1",`,
                `});`,
                `console.log(short_url);`,
            ].join("\n");
        case "react":
            return [
                `import { ShareButton } from "@socialpulse/splink";`,
                ``,
                `// On your ${t.slug} site — the API key stays on your server.`,
                `// Put a small proxy endpoint at /api/short that calls the SDK`,
                `// with process.env.${envName} and returns { short_url }.`,
                ``,
                `<ShareButton`,
                `  createShortLinkUrl="/api/short"`,
                `  longUrl="https://${t.slug}.example.com/page/1"`,
                `  title="Share this"`,
                `/>`,
            ].join("\n");
        case "py":
            return [
                `import os, requests`,
                ``,
                `SPLINK_API_URL = "${apiBase}"`,
                `SPLINK_API_KEY = os.environ["${envName}"]`,
                ``,
                `def create_short_link(long_url: str, alias: str | None = None):`,
                `    r = requests.post(`,
                `        f"{SPLINK_API_URL}/v1/links",`,
                `        headers={"X-API-Key": SPLINK_API_KEY},`,
                `        json={"long_url": long_url, "alias": alias},`,
                `        timeout=10,`,
                `    )`,
                `    r.raise_for_status()`,
                `    return r.json()`,
            ].join("\n");
        default:
            return "";
    }
}
